<template lang="pug">
.waiter-service-plan-tab(v-cloak)
  .mb-2
  .box-body(style="padding:0;")
    .register-body
      .register-row(v-show="0 < errorMessages.length")
        .register-group.error-message-content
          .register-value(v-for="errorMessage in errorMessages")
            .error-message {{ errorMessage }}

      .register-row(v-if="_useWaiterService")
        .register-group
          .register-key
            span.key-name 現在のプラン
          .register-value(v-if="0 <= currentPlanType")
            span.current-plan-name {{ getPlanName(currentPlanType) }}{{ _validPeriodFrom }}
      .register-row(v-else)
        .register-group
          .register-key
            span.key-name 現在のプラン
          .register-value
            span.current-plan-name.error-message 未契約

    .ads-service-content(v-if="!_useWaiterService")
      div 入所者募集・待機者管理ができる新しいサービスメニューです。
      ul
        li 入所者募集ページ作成機能（作成したページを公開）
        li 入所希望者がウェブサイトから入所申込み
        li 入所申込者（待機者）を施設アプリで管理
        li 入所評価判定や経過記録の管理
      div ご興味がございましたら、詳しいサービス内容をご説明させていただきます。
      div 
        span.link-text(@click="gotoSupport") 新規お問い合わせ
        span よりご連絡ください。

  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";

import WaiterServicePlansService, { WaiterServicePlansGetModel, WaiterServicePlanType } from "@/commons/services/WaiterServicePlans";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";

/**
 * ### 待機者管理サービスプラン確認・変更画面コンポーネント
 */
@Component({
  components: {
    RelativeLoading
  }
})
export default class WaiterServicePlanTab extends Vue {
  /** 待機者管理プランサービス */
  @Inject("WaiterServicePlansService") plansService!: WaiterServicePlansService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 現在のプラン種別 */
  currentPlanType: WaiterServicePlanType = WaiterServicePlanType.UNKNOWN;
  /** プラン情報のMAP */
  planMap: Map<WaiterServicePlanType, WaiterServicePlansGetModel> = new Map<WaiterServicePlanType, WaiterServicePlansGetModel>();

  /** フリープランを表示するか */
  showFreePlan: boolean = false;

  /** エラーメッセージ */
  errorMessages: string[] = [];

  get _useWaiterService(): boolean {
    return userStore.facility.useWaiterService && userStore.loginUser.permissions.functions.useWaiterService;
  }

  get _validPeriodFrom(): string {
    const now: number = Number(this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss") + "000");

    if (userStore.facility.waiterServiceValidPeriodFrom && now <= userStore.facility.waiterServiceValidPeriodFrom) {
      const year: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.waiterServiceValidPeriodFrom, "yyyy"));
      const month: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.waiterServiceValidPeriodFrom, "mm"));
      const day: number = Number(this.$dateUtil.convertNumberToString(userStore.facility.waiterServiceValidPeriodFrom, "dd"));
      let date: Date = new Date(year, month - 1, day);

      date.setDate(date.getDate() - 1);

      const periodFrom: string = this.$dateUtil.convertDateToString(date, "yyyy年mm月dd日", false);

      return `（${periodFrom}まで有料プランの無料評価期間）`;
    } else {
      return "";
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 待機者管理サービスを使用しない場合は画面表示処理は無いので即returnする
   */
  public async createView(): Promise<void> {
    if (!this._useWaiterService) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // エラーメッセージをリセットする
    this.errorMessages = [];

    this.currentPlanType = userStore.facility.waiterServicePlanType;

    const plans: Array<WaiterServicePlansGetModel> = await this.getPlans();

    if (userStore.facility.waiterServiceValidPeriodFrom) {
      const showFreePlan: boolean = this.judgeShowFreePlan(userStore.facility.waiterServiceValidPeriodFrom, this.currentPlanType);
      this.showFreePlan = showFreePlan;
    }

    const planMap: Map<WaiterServicePlanType, WaiterServicePlansGetModel> = this.createPlanMap(plans);
    this.planMap = planMap;

    this.isBusy = false;

    return;
  }

  /**
   * ### プランを取得する
   */
  public async getPlans(): Promise<Array<WaiterServicePlansGetModel>> {
    const response: Array<WaiterServicePlansGetModel> = await this.plansService.gets(userStore);
    return response;
  }

  /**
   * ### フリープランを表示するか否かを返す
   *
   * - 無料期間中であれば表示する
   * - 現在プランがフリープランであれば表示する
   * - それ以外は非表示にする
   */
  public judgeShowFreePlan(validPeriodFrom: number, currentPlanType: WaiterServicePlanType): boolean {
    const now: number = Number(this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss") + "000");
    if (now <= validPeriodFrom) {
      return true;
    }

    if (currentPlanType == WaiterServicePlanType.FREE) {
      return true;
    }

    return false;
  }

  /**
   * ### プラン情報のMAPを作成する
   */
  public createPlanMap(plans: Array<WaiterServicePlansGetModel>): Map<WaiterServicePlanType, WaiterServicePlansGetModel> {
    {
      let map: Map<WaiterServicePlanType, WaiterServicePlansGetModel> = new Map<WaiterServicePlanType, WaiterServicePlansGetModel>();

      for (const plan of plans) {
        map.set(plan.type, plan);
      }

      return map;
    }
  }

  /**
   * ### プラン名を返す
   */
  public getPlanName(planType: WaiterServicePlanType): string {
    const plan: WaiterServicePlansGetModel | undefined = this.planMap.get(planType);

    if (plan != undefined) {
      return plan.name;
    } else {
      return "";
    }
  }

  /**
   * ### 新規お問い合わせ画面へ遷移する
   */
  public gotoSupport(): void {
    this.$router.push({ name: PcUrl.SupportRegister, params: { type: "5" } });
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.waiter-service-plan-tab {
  max-width: 1150px;
  margin-left: auto;
  margin-right: auto;

  .request-plan {
    font-size: 18px;
    white-space: pre-wrap;
    color: $validation-label-font-color;
  }

  .plan-list-title {
    .text {
      font-size: 16px;
      margin-bottom: 12px;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .plan-detail-btn {
    margin-right: 10px;
  }
}
</style>
